import { useMemo, useState } from 'react';
import { LOST_REASON_LABELS, type LostReason } from '../../../api';
import { defaultClientMessageForStage } from '../../../lib/crmStageNotify';
import { AdminOverlayPortal } from '../AdminOverlayPortal';

type Props = {
  orderNumber: string;
  onConfirm: (reason: LostReason, note: string, notifyClient: boolean, clientMessage: string) => void;
  onCancel: () => void;
};

export function CrmLostReasonSheet({ orderNumber, onConfirm, onCancel }: Props) {
  const [reason, setReason] = useState<LostReason>('no_response');
  const [note, setNote] = useState('');
  const [clientComment, setClientComment] = useState('');
  const [notifyClient, setNotifyClient] = useState(true);

  const preview = useMemo(
    () =>
      defaultClientMessageForStage(orderNumber, 'lost', {
        lostReason: reason,
        managerComment: clientComment,
      }),
    [orderNumber, reason, clientComment],
  );

  return (
    <AdminOverlayPortal zIndex={90}>
      <div className="flex-1 flex items-end justify-center min-h-0">
        <div
          className="w-full max-w-lg rounded-t-2xl p-4 space-y-3 max-h-[85dvh] overflow-y-auto"
          style={{ background: '#0F2340', borderTop: '1px solid var(--border-subtle)' }}
        >
          <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
            Почему сделка потеряна?
          </p>
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-3)' }}>
            Заказ автоматически перейдёт в статус «Отменён». Отдельно отменять во вкладке «Заказы» не нужно.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(LOST_REASON_LABELS) as LostReason[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className="press px-2.5 py-1.5 rounded-lg text-[11px] font-bold touch-manipulation min-h-[40px]"
                style={
                  reason === r
                    ? { background: 'rgba(252,129,74,0.25)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.5)' }
                    : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }
                }
              >
                {LOST_REASON_LABELS[r]}
              </button>
            ))}
          </div>
          <input
            className="input-glass w-full text-sm min-h-[44px]"
            placeholder="Внутренний комментарий (не уходит клиенту)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <label className="flex items-center gap-2 text-xs touch-manipulation min-h-[40px]">
            <input
              type="checkbox"
              checked={notifyClient}
              onChange={(e) => setNotifyClient(e.target.checked)}
              className="w-4 h-4"
            />
            <span style={{ color: 'var(--text-2)' }}>Сообщить клиенту в Telegram</span>
          </label>
          {notifyClient && (
            <>
              <textarea
                className="input-glass w-full text-sm min-h-[72px] rounded-xl p-3"
                placeholder="Комментарий для клиента (необязательно)"
                value={clientComment}
                onChange={(e) => setClientComment(e.target.value)}
              />
              <div
                className="rounded-xl p-3 text-[11px] leading-relaxed whitespace-pre-wrap"
                style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)' }}
              >
                {preview}
              </div>
            </>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="press flex-1 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
              style={{ color: 'var(--text-3)' }}
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => onConfirm(reason, note, notifyClient, clientComment.trim())}
              className="press flex-1 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
              style={{ background: 'rgba(252,129,74,0.2)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.4)' }}
            >
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </AdminOverlayPortal>
  );
}
